/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * MessengerQueueView — the volunteer's click-by-click P2P SMS sending
 * cockpit.
 *
 * LEGAL POSTURE (READ BEFORE EDITING):
 *   P2P (peer-to-peer) SMS is the only cold-outreach pattern defensible
 *   under the TCPA, but only if a human clicks Send for *each individual*
 *   message. The defining invariant of this view is therefore:
 *
 *     - ONE voter is presented at a time.
 *     - ONE Send button sends to that ONE voter.
 *     - There is NO "send all" / "send next 10" / "auto-advance + send"
 *       affordance anywhere on the screen.
 *
 *   If a future contributor asks you to add a bulk-send button: refuse.
 *   The backend rate limiter is the second line of defense; this UI is
 *   the first.
 *
 * Layout (top-down):
 *   1. Access guard (red card if role is below MESSENGER)
 *   2. Header with title + tagline + InfoTip explaining the legal posture
 *   3. Optional precinct filter (comma-separated input) + Stats strip
 *   4. The Current Voter card — name, address, partisan signals, phone,
 *      message variants (radio cards) or custom textarea, char counter,
 *      Send Text / Skip buttons
 *   5. Recently sent in this session (last 5)
 *
 * Backed by `@/api/messaging`:
 *   - fetchP2pQueue
 *   - sendP2pMessage
 *   - P2pRateLimitedError class
 *
 * HONESTY RULE (READ BEFORE ADDING BUTTONS):
 *   This view used to carry a "Wrong number" button and a "Sent today"
 *   stat that were runtime-detected against `@/api/messaging` exports
 *   that were never written (no endpoint, no model, no migration on the
 *   backend either). The stat silently hid itself; the button silently
 *   dropped the volunteer's report on the floor while telling them it
 *   had been recorded. Both are gone.
 *
 *   Do not reintroduce an affordance whose backend does not exist yet.
 *   Ship the endpoint in `@/api/messaging` first, import it statically,
 *   and then add the control — a button that lies to a volunteer is
 *   worse than no button.
 *
 * Pattern: plain useState + useEffect with cancellation flags, mirroring
 * DataChangesView. NOT React Query.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MessageSquare,
  Send,
  SkipForward,
  CheckCircle2,
  AlertTriangle,
  PartyPopper,
  Inbox,
} from 'lucide-react';
import {
  fetchP2pQueue,
  sendP2pMessage,
  P2pRateLimitedError,
} from '@/api/messaging';
import type {
  P2pQueueItem,
  P2pQueueVoter,
  P2pQueueResponse,
  P2pSendRequest,
  P2pSendResponse,
} from '@/types/messaging';
import EmptyState from '@/components/EmptyState';
import InfoTip from '@/components/InfoTip';
import { ViewHeader } from '@/components/ViewHeader';
import { Skeleton } from '@/components/Skeleton';
import { roleMatches } from '@/lib/roles';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Allowed roles. Anything below MESSENGER hits the access-denied card. */
const ALLOWED_ROLES = [
  'MESSENGER',
  'SMS_MANAGER',
  'MANAGER',
  'ORG_MANAGER',
  'ADMIN',
] as const;

/** SMS segment thresholds — GSM-7 is 160 chars/segment; we keep it simple
 *  and don't try to detect unicode (the backend will pick the encoding). */
const SMS_SEGMENT_SIZE = 160;

/** When the visible queue gets this small, prefetch the next page. */
const PREFETCH_THRESHOLD = 5;

/** Default page size for queue fetches. */
const QUEUE_PAGE_SIZE = 20;

/** How many recently-sent rows we show in the bottom strip. */
const RECENT_SENT_LIMIT = 5;

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

interface RecentSend {
  voterId: number | string;
  name: string;
  sentAt: number; // Date.now()
}

interface Toast {
  id: number;
  tone: 'success' | 'info' | 'warning' | 'danger';
  text: string;
}

interface RateLimitBanner {
  message: string;
  retryAfterSeconds: number;
  window: string;
  blockUntil: number; // Date.now() + retryAfterSeconds * 1000
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = String(raw).replace(/\D/g, '');
  // Strip leading "1" if present so we always format as the 10-digit US shape.
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

function smsCharCount(body: string): { chars: number; segments: number } {
  const chars = body.length;
  if (chars === 0) return { chars: 0, segments: 0 };
  const segments = Math.ceil(chars / SMS_SEGMENT_SIZE);
  return { chars, segments };
}

function timeAgo(epochMs: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - epochMs) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function parsePrecinctFilter(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeRole(role: string | undefined): string {
  return typeof role === 'string' ? role.toUpperCase() : '';
}

// ---------------------------------------------------------------------------
// Variant resolution
//
// The queue item is expected to ship 1-3 "suggested" variants the backend
// has already rendered (merge fields expanded). We resolve them defensively
// so the view doesn't crash if a variant ships as `{ body }` vs a bare
// string. Custom message is always the last option.
// ---------------------------------------------------------------------------

interface ResolvedVariant {
  id: string;
  label: string;
  body: string;
}

function resolveVariants(item: P2pQueueItem | undefined): ResolvedVariant[] {
  if (!item) return [];
  // The backend has a few possible shapes for "suggested messages" depending
  // on whether the variant came from a campaign template or was generated
  // ad-hoc. Cover the realistic ones with a graceful fallback.
  const rawList: any[] = (item as any).variants
    ?? (item as any).suggested_messages
    ?? (item as any).suggested_variants
    ?? [];
  const resolved: ResolvedVariant[] = [];
  for (let i = 0; i < rawList.length && i < 5; i++) {
    const v = rawList[i];
    if (!v) continue;
    if (typeof v === 'string') {
      resolved.push({ id: `v${i}`, label: `Option ${i + 1}`, body: v });
    } else if (typeof v === 'object') {
      const body = String(v.body ?? v.rendered_body ?? v.text ?? '').trim();
      if (!body) continue;
      resolved.push({
        id: String(v.id ?? `v${i}`),
        label: String(v.label ?? v.name ?? `Option ${i + 1}`),
        body,
      });
    }
  }
  return resolved;
}

function resolveVoter(item: P2pQueueItem | undefined): P2pQueueVoter | null {
  if (!item) return null;
  // Some envelopes nest the voter; some put fields at the top level.
  const v: any = (item as any).voter ?? item;
  return v as P2pQueueVoter;
}

function voterName(v: P2pQueueVoter | null): string {
  if (!v) return 'voter';
  const anyV = v as any;
  if (anyV.full_name) return String(anyV.full_name);
  const first = anyV.first_name ?? '';
  const last = anyV.last_name ?? '';
  const composed = `${first} ${last}`.trim();
  return composed || String(anyV.voter_id ?? anyV.id ?? 'voter');
}

function voterPhone(v: P2pQueueVoter | null): string | null {
  if (!v) return null;
  const anyV = v as any;
  return anyV.phone ?? anyV.phone_number ?? anyV.cell_phone ?? null;
}

function voterPartisanLabel(v: P2pQueueVoter | null): string | null {
  if (!v) return null;
  const anyV = v as any;
  return anyV.calculated_party_label ?? anyV.party_label ?? anyV.party ?? null;
}

function voterAddress(v: P2pQueueVoter | null): string {
  if (!v) return '—';
  const anyV = v as any;
  const street = anyV.address ?? anyV.street_address ?? '';
  const city = anyV.city ?? '';
  const zip = anyV.zip_code ?? anyV.zip ?? '';
  return [street, [city, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—';
}

function voterPrecinct(v: P2pQueueVoter | null): string | null {
  if (!v) return null;
  const anyV = v as any;
  return anyV.precinct_num ?? anyV.precinct ?? null;
}

function voterId(v: P2pQueueVoter | null): number | string | null {
  if (!v) return null;
  const anyV = v as any;
  return anyV.voter_id ?? anyV.id ?? null;
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export interface MessengerQueueViewProps {
  userRole?: string;
  currentUser?: { id: number; full_name?: string };
  onBack?: () => void;
}

export function MessengerQueueView({
  userRole,
  currentUser: _currentUser,
  onBack: _onBack,
}: MessengerQueueViewProps) {
  // ============================================================================
  // Access guard — defense in depth.
  // Backend enforces role server-side; this stops a stale token slipping past
  // the nav filter.
  // ============================================================================
  const roleNorm = normalizeRole(userRole);
  const allowed = roleMatches(roleNorm, ALLOWED_ROLES);
  if (!allowed) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access Denied</h2>
          <p className="text-fg-secondary">
            The Send Texts queue is available to messengers and campaign
            leadership only. If you believe this is a mistake, contact your
            Org Manager.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // State
  // ============================================================================
  const [queue, setQueue] = useState<P2pQueueItem[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimitBanner | null>(null);

  // Audience filter — comma-separated precinct list, default all.
  const [precinctInput, setPrecinctInput] = useState('');
  const [precinctFilter, setPrecinctFilter] = useState<string[]>([]);

  // The selected message body for the current voter.
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [customBody, setCustomBody] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  // Stats. "Sent today" / "Replies" would need a backend counter endpoint
  // that does not exist; the only honest counters here are session-local.
  // `sentCount` is a true running total — `recentSends` is capped at
  // RECENT_SENT_LIMIT and must never be used as one.
  const [sentCount, setSentCount] = useState(0);
  const [skipCount, setSkipCount] = useState(0);

  // Recently sent (session-local)
  const [recentSends, setRecentSends] = useState<RecentSend[]>([]);

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastSeq = useRef(0);

  // In-flight send guard so double-clicks can't double-send.
  const [sending, setSending] = useState(false);

  // Tick to keep "time ago" labels fresh + rate-limit countdown.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  // ============================================================================
  // Derived data — the "current voter" is queue[0].
  // ============================================================================
  const currentItem = queue[0];
  const currentVoter = resolveVoter(currentItem);
  const variants = useMemo(() => resolveVariants(currentItem), [currentItem]);

  // Whenever the current voter changes, reset the message selection. Default
  // to the first variant if available; otherwise open the custom textarea.
  useEffect(() => {
    if (!currentItem) {
      setSelectedVariantId(null);
      setCustomBody('');
      setIsCustom(false);
      return;
    }
    if (variants.length > 0) {
      setSelectedVariantId(variants[0].id);
      setIsCustom(false);
      setCustomBody('');
    } else {
      setSelectedVariantId(null);
      setIsCustom(true);
      setCustomBody('');
    }
    // Only re-fire when the voter changes, not on every variant memo identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voterId(currentVoter)]);

  // The body that would be sent right now.
  const activeBody = isCustom
    ? customBody
    : variants.find((v) => v.id === selectedVariantId)?.body ?? '';

  const { chars, segments } = smsCharCount(activeBody);
  const segmentLabel = segments <= 1 ? '1 SMS' : `${segments} SMS segments`;

  // ============================================================================
  // Toast helpers
  // ============================================================================
  const pushToast = useCallback((tone: Toast['tone'], text: string) => {
    const id = ++toastSeq.current;
    setToasts((prev) => [...prev, { id, tone, text }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  // ============================================================================
  // Queue loading
  // ============================================================================
  const loadInitialQueue = useCallback(async () => {
    setLoadingInitial(true);
    setErrorBanner(null);
    setExhausted(false);
    try {
      const audienceArg = precinctFilter.length > 0 ? precinctFilter.join(',') : undefined;
      const res: P2pQueueResponse = await fetchP2pQueue({
        limit: QUEUE_PAGE_SIZE,
        audience: audienceArg,
      });
      const items: P2pQueueItem[] = (res as any)?.items ?? (res as any)?.queue ?? [];
      setQueue(items);
      if (items.length === 0) setExhausted(true);
    } catch (e: any) {
      const status = e?.response?.status;
      setErrorBanner(
        status
          ? `Could not load your queue (backend error ${status}).`
          : 'Could not reach the messaging backend.',
      );
    } finally {
      setLoadingInitial(false);
    }
  }, [precinctFilter]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || exhausted) return;
    setLoadingMore(true);
    try {
      const audienceArg = precinctFilter.length > 0 ? precinctFilter.join(',') : undefined;
      const res: P2pQueueResponse = await fetchP2pQueue({
        limit: QUEUE_PAGE_SIZE,
        audience: audienceArg,
      });
      const items: P2pQueueItem[] = (res as any)?.items ?? (res as any)?.queue ?? [];
      if (items.length === 0) {
        setExhausted(true);
      } else {
        // Dedupe by voter id so we don't double-up if the backend hands us
        // the same head a second time.
        setQueue((prev) => {
          const seen = new Set(
            prev
              .map((p) => voterId(resolveVoter(p)))
              .filter((id) => id !== null) as Array<number | string>,
          );
          const fresh = items.filter((it) => {
            const id = voterId(resolveVoter(it));
            if (id === null) return true;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          return prev.concat(fresh);
        });
      }
    } catch {
      // Background prefetch failure is silent; the user will see "exhausted"
      // when the visible queue empties out, which is the right escape hatch.
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, exhausted, precinctFilter]);

  // Initial load + refetch when the precinct filter changes.
  useEffect(() => {
    loadInitialQueue();
  }, [loadInitialQueue]);

  // Background prefetch when the visible queue gets shallow.
  useEffect(() => {
    if (loadingInitial) return;
    if (queue.length > 0 && queue.length <= PREFETCH_THRESHOLD) {
      fetchMore();
    }
  }, [queue.length, loadingInitial, fetchMore]);

  // ============================================================================
  // Queue mutation helpers
  // ============================================================================
  const shiftQueue = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  const recordSend = useCallback((id: number | string, name: string) => {
    setSentCount((n) => n + 1);
    setRecentSends((prev) =>
      [{ voterId: id, name, sentAt: Date.now() }, ...prev].slice(0, RECENT_SENT_LIMIT),
    );
  }, []);

  // ============================================================================
  // Rate-limit countdown — block Send until blockUntil expires.
  // ============================================================================
  const rateLimitRemainingSeconds = useMemo(() => {
    if (!rateLimit) return 0;
    const remaining = Math.max(0, Math.ceil((rateLimit.blockUntil - Date.now()) / 1000));
    return remaining;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLimit, tick]);

  useEffect(() => {
    if (rateLimit && rateLimitRemainingSeconds === 0) {
      setRateLimit(null);
    }
  }, [rateLimit, rateLimitRemainingSeconds]);

  // ============================================================================
  // Send action — THE central action. One voter, one click, one message.
  // No bulk-send path exists; do not add one.
  // ============================================================================
  const canSend = useMemo(() => {
    if (sending) return false;
    if (rateLimit && rateLimitRemainingSeconds > 0) return false;
    if (!currentVoter) return false;
    if (!voterPhone(currentVoter)) return false;
    if (!activeBody.trim()) return false;
    return true;
  }, [sending, rateLimit, rateLimitRemainingSeconds, currentVoter, activeBody]);

  const handleSend = useCallback(async () => {
    if (!canSend || !currentVoter) return;
    const id = voterId(currentVoter);
    if (id === null) {
      pushToast('warning', "This voter doesn't have an ID — skipping.");
      shiftQueue();
      return;
    }
    const name = voterName(currentVoter);
    const payload: P2pSendRequest = {
      voter_id: id as any,
      body: activeBody,
    } as P2pSendRequest;

    setSending(true);
    try {
      const res: P2pSendResponse = await sendP2pMessage(payload);
      void res; // backend ack — nothing else to do with it here
      recordSend(id, name);
      shiftQueue();
      pushToast('success', `Sent to ${name}`);
    } catch (e: any) {
      if (e instanceof P2pRateLimitedError) {
        const retryAfter = e.retryAfterSeconds || 30;
        setRateLimit({
          message: `You're sending too fast. Try again in ${retryAfter} seconds.`,
          retryAfterSeconds: retryAfter,
          window: e.window || 'minute',
          blockUntil: Date.now() + retryAfter * 1000,
        });
        // Do NOT shift — we want the volunteer to retry on the same voter.
      } else {
        const status = e?.response?.status;
        if (status === 409) {
          // Opt-out — pulled from under us. Skip and explain.
          shiftQueue();
          pushToast('info', `${name} opted out — skipped`);
        } else if (status === 400) {
          const reason = e?.response?.data?.detail ?? e?.response?.data?.error ?? 'invalid';
          shiftQueue();
          pushToast('warning', `Couldn't text ${name} (${reason})`);
        } else {
          // Don't shift on unknown errors — preserve the voter so the
          // volunteer can retry once they fix whatever's wrong.
          setErrorBanner(
            status
              ? `Send failed (backend error ${status}). The voter is preserved — try again.`
              : 'Send failed — could not reach the messaging backend. The voter is preserved — try again.',
          );
        }
      }
    } finally {
      setSending(false);
    }
  }, [canSend, currentVoter, activeBody, pushToast, recordSend, shiftQueue]);

  // ============================================================================
  // Skip action.
  //
  // This is the ONLY "move past this voter" control, and it is deliberately
  // session-local: nothing is persisted, and the UI says so. A dead phone
  // number is skipped like any other pass. When a wrong-number ledger exists
  // server-side, add a distinct control then — not before.
  // ============================================================================
  const handleSkip = useCallback(() => {
    if (!currentVoter) return;
    setSkipCount((n) => n + 1);
    shiftQueue();
  }, [currentVoter, shiftQueue]);

  // ============================================================================
  // Audience filter handlers
  // ============================================================================
  const applyPrecinctFilter = useCallback(() => {
    const parsed = parsePrecinctFilter(precinctInput);
    setPrecinctFilter(parsed);
  }, [precinctInput]);

  const clearPrecinctFilter = useCallback(() => {
    setPrecinctInput('');
    setPrecinctFilter([]);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-status-info" />
            Send Texts
            <InfoTip
              term={{
                title: 'Why one at a time?',
                short:
                  'Each message requires your individual click. This is how we comply with TCPA peer-to-peer SMS rules — no automated bulk sends, no "fire-and-forget" lists.',
              }}
              compact
            />
          </span>
        }
        tagline="Click-by-click. One voter at a time."
        tone="info"
      />

      {/* Precinct filter + Stats strip */}
      <div className="bg-bg-raised rounded-xl border border-border-base p-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex-1 min-w-0">
          <label className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
            Audience (optional)
          </label>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={precinctInput}
              onChange={(e) => setPrecinctInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyPrecinctFilter();
                }
              }}
              placeholder="Precincts, comma-separated (e.g. 101, 102) — leave blank for all"
              className="flex-1 min-w-[260px] bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
              aria-label="Filter queue by precinct"
            />
            <button
              type="button"
              onClick={applyPrecinctFilter}
              className="px-3 py-2 rounded bg-bg-elevated hover:bg-border-subtle text-fg-primary text-sm font-semibold"
            >
              Apply
            </button>
            {precinctFilter.length > 0 && (
              <button
                type="button"
                onClick={clearPrecinctFilter}
                className="px-3 py-2 rounded text-fg-muted hover:text-fg-primary text-sm"
              >
                Clear ({precinctFilter.length})
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-stretch gap-2">
          <StatPill label="In queue" value={queue.length.toLocaleString()} tone="info" />
          <StatPill
            label="Sent this session"
            value={sentCount.toLocaleString()}
            tone="success"
          />
          <StatPill label="Session skips" value={skipCount.toLocaleString()} tone="muted" />
        </div>
      </div>

      {/* Rate-limit banner */}
      {rateLimit && rateLimitRemainingSeconds > 0 && (
        <div className="bg-status-warning-bg/30 rounded-xl p-4 border border-status-warning-bg flex items-start gap-3 text-status-warning">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 text-sm">
            <div className="font-semibold">
              Slow down — sending too fast.
            </div>
            <div className="mt-0.5 text-fg-secondary">
              Try again in <span className="font-bold tabular-nums">{rateLimitRemainingSeconds}s</span>.
              (Per-{rateLimit.window} rate limit.)
            </div>
          </div>
        </div>
      )}

      {/* Error banner */}
      {errorBanner && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{errorBanner}</span>
          <button
            onClick={() => {
              setErrorBanner(null);
              loadInitialQueue();
            }}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loadingInitial && (
        <div className="bg-bg-raised rounded-xl border border-border-base p-6 space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {/* Exhausted / empty state */}
      {!loadingInitial && !errorBanner && queue.length === 0 && (
        <EmptyState
          icon={PartyPopper}
          title="All caught up!"
          description="No more voters in your queue. Check back in an hour, or expand your audience filter."
          action={
            precinctFilter.length > 0
              ? { label: 'Clear precinct filter', onClick: clearPrecinctFilter }
              : { label: 'Refresh queue', onClick: loadInitialQueue }
          }
        />
      )}

      {/* Current voter card */}
      {!loadingInitial && currentVoter && currentItem && (
        <CurrentVoterCard
          voter={currentVoter}
          variants={variants}
          selectedVariantId={selectedVariantId}
          isCustom={isCustom}
          customBody={customBody}
          chars={chars}
          segments={segments}
          segmentLabel={segmentLabel}
          canSend={canSend}
          sending={sending}
          rateLimited={Boolean(rateLimit && rateLimitRemainingSeconds > 0)}
          onPickVariant={(id) => {
            setSelectedVariantId(id);
            setIsCustom(false);
          }}
          onPickCustom={() => {
            setIsCustom(true);
            setSelectedVariantId(null);
            // Seed custom from the previously-selected variant so the
            // volunteer can lightly edit without retyping.
            if (!customBody) {
              const seed = variants[0]?.body ?? '';
              setCustomBody(seed);
            }
          }}
          onChangeCustom={setCustomBody}
          onSend={handleSend}
          onSkip={handleSkip}
        />
      )}

      {/* Recently sent */}
      {recentSends.length > 0 && (
        <RecentSendsList sends={recentSends} />
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div
          className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2"
          aria-live="polite"
          aria-atomic="true"
        >
          {toasts.map((t) => (
            <ToastChip key={t.id} toast={t} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'info' | 'success' | 'accent' | 'muted';
}) {
  const palette: Record<typeof tone, { bg: string; border: string; text: string }> = {
    info: {
      bg: 'bg-status-info-bg/25',
      border: 'border-status-info-bg/50',
      text: 'text-status-info',
    },
    success: {
      bg: 'bg-status-success-bg/25',
      border: 'border-status-success-bg/50',
      text: 'text-status-success',
    },
    accent: {
      bg: 'bg-accent/15',
      border: 'border-accent/40',
      text: 'text-accent-fg',
    },
    muted: {
      bg: 'bg-bg-elevated',
      border: 'border-border-base',
      text: 'text-fg-secondary',
    },
  };
  const p = palette[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${p.bg} ${p.border}`}>
      <div className={`text-[10px] uppercase tracking-wider ${p.text}`}>{label}</div>
      <div className="text-lg font-bold text-fg-primary tabular-nums leading-tight">{value}</div>
    </div>
  );
}

function CurrentVoterCard({
  voter,
  variants,
  selectedVariantId,
  isCustom,
  customBody,
  chars,
  segments,
  segmentLabel,
  canSend,
  sending,
  rateLimited,
  onPickVariant,
  onPickCustom,
  onChangeCustom,
  onSend,
  onSkip,
}: {
  voter: P2pQueueVoter;
  variants: ResolvedVariant[];
  selectedVariantId: string | null;
  isCustom: boolean;
  customBody: string;
  chars: number;
  segments: number;
  segmentLabel: string;
  canSend: boolean;
  sending: boolean;
  rateLimited: boolean;
  onPickVariant: (id: string) => void;
  onPickCustom: () => void;
  onChangeCustom: (body: string) => void;
  onSend: () => void;
  onSkip: () => void;
}) {
  const name = voterName(voter);
  const phone = voterPhone(voter);
  const partisan = voterPartisanLabel(voter);
  const precinct = voterPrecinct(voter);

  return (
    <div className="bg-bg-raised rounded-xl border border-border-base p-6 space-y-5 shadow-ds-sm">
      {/* Voter identity */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-fg-primary truncate">{name}</h2>
          <div className="text-sm text-fg-secondary mt-1">{voterAddress(voter)}</div>
          <div className="flex items-center gap-3 mt-2 text-xs text-fg-muted flex-wrap">
            {precinct && (
              <span>
                Precinct <span className="text-fg-primary font-semibold">{precinct}</span>
              </span>
            )}
            {partisan && (
              <span>
                Partisan signal{' '}
                <span className="text-fg-primary font-semibold">{partisan}</span>
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
            Phone
          </div>
          <div className="text-lg font-bold text-fg-primary tabular-nums">
            {formatPhone(phone)}
          </div>
          {!phone && (
            <div className="text-xs text-status-warning mt-1">
              No phone on file — skip this voter.
            </div>
          )}
        </div>
      </div>

      {/* Message variants */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
          Choose a message — one click, one voter
        </div>

        {variants.length === 0 && !isCustom && (
          <div className="text-sm text-fg-muted italic">
            No suggested messages — type a custom message below.
          </div>
        )}

        {variants.map((v) => (
          <VariantRadio
            key={v.id}
            label={v.label}
            body={v.body}
            selected={!isCustom && selectedVariantId === v.id}
            onSelect={() => onPickVariant(v.id)}
          />
        ))}

        <CustomRadio
          selected={isCustom}
          body={customBody}
          onSelect={onPickCustom}
          onChangeBody={onChangeCustom}
        />
      </div>

      {/* Char counter */}
      <div className="flex items-center justify-between text-xs">
        <div className="text-fg-muted">
          <span className="tabular-nums text-fg-secondary font-semibold">{chars}</span>{' '}
          characters
          {chars > 0 && (
            <>
              {' '}·{' '}
              <span className={segments > 1 ? 'text-status-warning font-semibold' : ''}>
                {segmentLabel}
              </span>
            </>
          )}
        </div>
        {segments > 1 && (
          <div className="text-status-warning text-[11px]">
            Long messages cost more. Consider trimming.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-accent hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-base py-4 transition-colors min-h-12 shadow-ds-sm"
          aria-label={`Send text to ${name}`}
        >
          <Send className="w-5 h-5" aria-hidden="true" />
          {sending ? 'Sending…' : rateLimited ? 'Rate-limited — wait…' : `Send text to ${name}`}
        </button>

        <button
          type="button"
          onClick={onSkip}
          disabled={sending}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary font-semibold text-sm py-2.5"
        >
          <SkipForward className="w-4 h-4" aria-hidden="true" />
          Skip this voter
        </button>

        <p className="text-[11px] text-fg-muted text-center">
          One click sends one message to one voter. No bulk send. (TCPA P2P
          compliance.)
        </p>
        <p className="text-[11px] text-fg-muted text-center">
          Wrong or disconnected number? Skip it and tell your Org Manager
          — skips are not saved, so bad numbers can&apos;t be reported from
          here yet.
        </p>
      </div>
    </div>
  );
}

function VariantRadio({
  label,
  body,
  selected,
  onSelect,
}: {
  label: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
        selected
          ? 'border-accent bg-accent/10 ring-2 ring-accent/40'
          : 'border-border-base bg-bg-base hover:bg-bg-elevated'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 inline-flex w-4 h-4 rounded-full border-2 flex-shrink-0 items-center justify-center ${
            selected ? 'border-accent' : 'border-border-subtle'
          }`}
          aria-hidden="true"
        >
          {selected && <span className="w-2 h-2 rounded-full bg-accent" />}
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold mb-1">
            {label}
          </div>
          <div className="text-sm text-fg-primary whitespace-pre-wrap break-words">
            {body}
          </div>
        </div>
      </div>
    </button>
  );
}

function CustomRadio({
  selected,
  body,
  onSelect,
  onChangeBody,
}: {
  selected: boolean;
  body: string;
  onSelect: () => void;
  onChangeBody: (b: string) => void;
}) {
  return (
    <div
      className={`rounded-lg border px-4 py-3 transition-colors ${
        selected
          ? 'border-accent bg-accent/10 ring-2 ring-accent/40'
          : 'border-border-base bg-bg-base'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="w-full flex items-start gap-3 text-left"
      >
        <span
          className={`mt-0.5 inline-flex w-4 h-4 rounded-full border-2 flex-shrink-0 items-center justify-center ${
            selected ? 'border-accent' : 'border-border-subtle'
          }`}
          aria-hidden="true"
        >
          {selected && <span className="w-2 h-2 rounded-full bg-accent" />}
        </span>
        <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
          Custom message
        </div>
      </button>
      {selected && (
        <div className="mt-2">
          <textarea
            value={body}
            onChange={(e) => onChangeBody(e.target.value)}
            rows={4}
            placeholder="Type your message…"
            aria-label="Custom message body"
            className="w-full bg-bg-base border border-border-base rounded p-3 text-sm text-fg-primary placeholder-fg-muted focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
      )}
    </div>
  );
}

function RecentSendsList({ sends }: { sends: RecentSend[] }) {
  return (
    <div className="bg-bg-raised rounded-xl border border-border-base p-4">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold mb-2">
        Recently sent in this session
      </div>
      {sends.length === 0 ? (
        <div className="text-sm text-fg-muted flex items-center gap-2">
          <Inbox className="w-4 h-4" aria-hidden="true" />
          No sends yet.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {sends.map((s) => (
            <li
              key={`${s.voterId}-${s.sentAt}`}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="flex items-center gap-2 min-w-0">
                <CheckCircle2
                  className="w-4 h-4 text-status-success flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="text-fg-primary truncate">Sent to {s.name}</span>
              </span>
              <span className="text-xs text-fg-muted tabular-nums flex-shrink-0">
                {timeAgo(s.sentAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ToastChip({ toast }: { toast: Toast }) {
  const palette: Record<Toast['tone'], string> = {
    success: 'bg-status-success-bg/90 border-status-success-bg text-white',
    info: 'bg-status-info-bg/90 border-status-info-bg text-white',
    warning: 'bg-status-warning-bg/90 border-status-warning-bg text-white',
    danger: 'bg-status-danger-bg/90 border-status-danger-bg text-white',
  };
  return (
    <div
      role="status"
      className={`rounded-lg border px-4 py-2 text-sm font-semibold shadow-2xl shadow-black/60 ${palette[toast.tone]}`}
    >
      {toast.text}
    </div>
  );
}

export default MessengerQueueView;
